import {
  EnvironmentProviders,
  InjectionToken,
  Optional,
  makeEnvironmentProviders,
} from '@angular/core';
import type { Firestore as FbFirestore } from 'firebase/firestore';
import {
  addDoc as rawAddDoc,
  aggregateFieldEqual as rawAggregateFieldEqual,
  aggregateQuerySnapshotEqual as rawAggregateQuerySnapshotEqual,
  and as rawAnd,
  clearIndexedDbPersistence as rawClearIndexedDbPersistence,
  collection as rawCollection,
  collectionGroup as rawCollectionGroup,
  connectFirestoreEmulator as rawConnectFirestoreEmulator,
  deleteAllPersistentCacheIndexes as rawDeleteAllPersistentCacheIndexes,
  deleteDoc as rawDeleteDoc,
  deleteField as rawDeleteField,
  disableNetwork as rawDisableNetwork,
  disablePersistentCacheIndexAutoCreation as rawDisablePersistentCacheIndexAutoCreation,
  doc as rawDoc,
  documentId as rawDocumentId,
  enableIndexedDbPersistence as rawEnableIndexedDbPersistence,
  enableMultiTabIndexedDbPersistence as rawEnableMultiTabIndexedDbPersistence,
  enableNetwork as rawEnableNetwork,
  enablePersistentCacheIndexAutoCreation as rawEnablePersistentCacheIndexAutoCreation,
  endAt as rawEndAt,
  endBefore as rawEndBefore,
  getAggregateFromServer as rawGetAggregateFromServer,
  getCountFromServer as rawGetCountFromServer,
  getDoc as rawGetDoc,
  getDocFromCache as rawGetDocFromCache,
  getDocFromServer as rawGetDocFromServer,
  getDocs as rawGetDocs,
  getDocsFromCache as rawGetDocsFromCache,
  getDocsFromServer as rawGetDocsFromServer,
  getFirestore as rawGetFirestore,
  getPersistentCacheIndexManager as rawGetPersistentCacheIndexManager,
  increment as rawIncrement,
  initializeFirestore as rawInitializeFirestore,
  limit as rawLimit,
  limitToLast as rawLimitToLast,
  loadBundle as rawLoadBundle,
  namedQuery as rawNamedQuery,
  onSnapshot as rawOnSnapshot,
  onSnapshotsInSync as rawOnSnapshotsInSync,
  or as rawOr,
  orderBy as rawOrderBy,
  query as rawQuery,
  queryEqual as rawQueryEqual,
  refEqual as rawRefEqual,
  runTransaction as rawRunTransaction,
  setDoc as rawSetDoc,
  setIndexConfiguration as rawSetIndexConfiguration,
  setLogLevel as rawSetLogLevel,
  snapshotEqual as rawSnapshotEqual,
  startAfter as rawStartAfter,
  startAt as rawStartAt,
  sum as rawSum,
  terminate as rawTerminate,
  updateDoc as rawUpdateDoc,
  vector as rawVector,
  waitForPendingWrites as rawWaitForPendingWrites,
  where as rawWhere,
  writeBatch as rawWriteBatch,
} from 'firebase/firestore';
import { kdvWrap } from '@fido/firebase';
import { FirebaseApp } from '@fido/firebase/app';
import { AppCheck } from '@fido/firebase/app-check';
import { Auth } from '@fido/firebase/auth';
import {
  collectionData as vendorCollectionData,
  collectionChanges as vendorCollectionChanges,
  sortedChanges as vendorSortedChanges,
  auditTrail as vendorAuditTrail,
  collection as vendorCollectionSnapshots,
  collectionCountSnap as vendorCollectionCountSnap,
  collectionCount as vendorCollectionCount,
} from './lib/vendor/collection';
import {
  docData as vendorDocData,
  doc as vendorDocSnapshots,
  snapToData,
} from './lib/vendor/document';
import { fromRef as vendorFromRef } from './lib/vendor/from-ref';

// 1) Wrapped firebase/firestore functions. All use blockUntilFirst=true,
//    matching @angular/fire's _zoneWrap declarations from
//    angular-fire-firestore.mjs.
export const addDoc = kdvWrap(rawAddDoc, true);
export const aggregateFieldEqual = kdvWrap(rawAggregateFieldEqual, true);
export const aggregateQuerySnapshotEqual = kdvWrap(rawAggregateQuerySnapshotEqual, true);
export const and = kdvWrap(rawAnd, true);
export const clearIndexedDbPersistence = kdvWrap(rawClearIndexedDbPersistence, true);
export const collection = kdvWrap(rawCollection, true);
export const collectionGroup = kdvWrap(rawCollectionGroup, true);
export const connectFirestoreEmulator = kdvWrap(rawConnectFirestoreEmulator, true);
export const deleteAllPersistentCacheIndexes = kdvWrap(rawDeleteAllPersistentCacheIndexes, true);
export const deleteDoc = kdvWrap(rawDeleteDoc, true);
export const deleteField = kdvWrap(rawDeleteField, true);
export const disableNetwork = kdvWrap(rawDisableNetwork, true);
export const disablePersistentCacheIndexAutoCreation = kdvWrap(
  rawDisablePersistentCacheIndexAutoCreation,
  true,
);
export const doc = kdvWrap(rawDoc, true);
export const documentId = kdvWrap(rawDocumentId, true);
export const enableIndexedDbPersistence = kdvWrap(rawEnableIndexedDbPersistence, true);
export const enableMultiTabIndexedDbPersistence = kdvWrap(
  rawEnableMultiTabIndexedDbPersistence,
  true,
);
export const enableNetwork = kdvWrap(rawEnableNetwork, true);
export const enablePersistentCacheIndexAutoCreation = kdvWrap(
  rawEnablePersistentCacheIndexAutoCreation,
  true,
);
export const endAt = kdvWrap(rawEndAt, true);
export const endBefore = kdvWrap(rawEndBefore, true);
export const getAggregateFromServer = kdvWrap(rawGetAggregateFromServer, true);
export const getCountFromServer = kdvWrap(rawGetCountFromServer, true);
export const getDoc = kdvWrap(rawGetDoc, true);
export const getDocFromCache = kdvWrap(rawGetDocFromCache, true);
export const getDocFromServer = kdvWrap(rawGetDocFromServer, true);
export const getDocs = kdvWrap(rawGetDocs, true);
export const getDocsFromCache = kdvWrap(rawGetDocsFromCache, true);
export const getDocsFromServer = kdvWrap(rawGetDocsFromServer, true);
export const getFirestore = kdvWrap(rawGetFirestore, true);
export const getPersistentCacheIndexManager = kdvWrap(rawGetPersistentCacheIndexManager, true);
export const increment = kdvWrap(rawIncrement, true);
export const initializeFirestore = kdvWrap(rawInitializeFirestore, true);
export const limit = kdvWrap(rawLimit, true);
export const limitToLast = kdvWrap(rawLimitToLast, true);
export const loadBundle = kdvWrap(rawLoadBundle, true);
export const namedQuery = kdvWrap(rawNamedQuery, true);
export const onSnapshot = kdvWrap(rawOnSnapshot, true);
export const onSnapshotsInSync = kdvWrap(rawOnSnapshotsInSync, true);
export const or = kdvWrap(rawOr, true);
export const orderBy = kdvWrap(rawOrderBy, true);
export const query = kdvWrap(rawQuery, true);
export const queryEqual = kdvWrap(rawQueryEqual, true);
export const refEqual = kdvWrap(rawRefEqual, true);
export const runTransaction = kdvWrap(rawRunTransaction, true);
export const setDoc = kdvWrap(rawSetDoc, true);
export const setIndexConfiguration = kdvWrap(rawSetIndexConfiguration, true);
export const setLogLevel = kdvWrap(rawSetLogLevel, true);
export const snapshotEqual = kdvWrap(rawSnapshotEqual, true);
export const startAfter = kdvWrap(rawStartAfter, true);
export const startAt = kdvWrap(rawStartAt, true);
export const sum = kdvWrap(rawSum, true);
export const terminate = kdvWrap(rawTerminate, true);
export const updateDoc = kdvWrap(rawUpdateDoc, true);
export const vector = kdvWrap(rawVector, true);
export const waitForPendingWrites = kdvWrap(rawWaitForPendingWrites, true);
export const where = kdvWrap(rawWhere, true);
export const writeBatch = kdvWrap(rawWriteBatch, true);

// 2) Re-exports — value-object classes, frozen FieldValue factories, and
//    persistent-cache builder functions that @angular/fire intentionally
//    leaves unwrapped (no SSR pending-task value).
export {
  // Value-object classes
  Timestamp,
  GeoPoint,
  Bytes,
  FieldPath,
  FieldValue,
  // FieldValue factories — pure constructors of sentinel values
  arrayUnion,
  arrayRemove,
  serverTimestamp,
  // Aggregate factories — `sum` IS wrapped above (per upstream); these
  // two were added later in firebase/firestore and aren't wrapped upstream.
  count,
  average,
  // Persistent-cache configurators
  persistentLocalCache,
  memoryLocalCache,
  persistentMultipleTabManager,
  persistentSingleTabManager,
} from 'firebase/firestore';
export type {
  CollectionReference,
  DocumentReference,
  Query,
  DocumentSnapshot,
  QuerySnapshot,
  QueryDocumentSnapshot,
  DocumentData,
  QueryConstraint,
  SnapshotOptions,
  SnapshotMetadata,
  FirestoreDataConverter,
  SetOptions,
  UpdateData,
  WhereFilterOp,
  OrderByDirection,
  AggregateQuerySnapshot,
  AggregateField,
  DocumentChange,
  DocumentChangeType,
} from 'firebase/firestore';

// 3) DI token (declaration-merged with type alias). Same name as @angular/fire
//    so consumer call sites like `inject(Firestore)` keep compiling unchanged.
export type Firestore = FbFirestore;
export const Firestore = new InjectionToken<FbFirestore>('@fido/firebase.firestore');

export function provideFirestore(factory: () => FbFirestore): EnvironmentProviders {
  return makeEnvironmentProviders([
    {
      provide: Firestore,
      // FirebaseApp + (Optional) Auth + (Optional) AppCheck — mirrors
      // @angular/fire's provideFirestore multi-instance deps. Comment from
      // upstream: "Firestore+Auth work better if Auth is loaded first."
      // AppCheck dep gives token mint a head start so reads/writes
      // don't race against AppCheck warmup.
      useFactory: (_app: unknown, _auth: unknown, _appCheck: unknown) => factory(),
      deps: [FirebaseApp, [new Optional(), Auth], [new Optional(), AppCheck]],
    },
  ]);
}

// 4) Wrapped helpers (rxfire-equivalent). Each is the vendored rxfire
//    helper passed through kdvWrap so subscriptions hold a PendingTask
//    until first emission and listener callbacks run inside the captured
//    EnvironmentInjector. Identical surface to @angular/fire/firestore.
export const collectionData = kdvWrap(vendorCollectionData, true);
export const collectionChanges = kdvWrap(vendorCollectionChanges, true);
export const collectionSnapshots = kdvWrap(vendorCollectionSnapshots, true);
export const collectionCountSnap = kdvWrap(vendorCollectionCountSnap, false);
export const collectionCount = kdvWrap(vendorCollectionCount, false);
export const sortedChanges = kdvWrap(vendorSortedChanges, true);
export const auditTrail = kdvWrap(vendorAuditTrail, true);
export const docData = kdvWrap(vendorDocData, true);
export const docSnapshots = kdvWrap(vendorDocSnapshots, true);
export const fromRef = kdvWrap(vendorFromRef, true);

// snapToData is a pure function (no listener), exposed for converter authors.
export { snapToData };
